import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { I18n } from 'react-redux-i18n-lite';
import dayjs from 'dayjs';
import CircularProgress from '@material-ui/core/CircularProgress';
import {
  AnnotationTypeIcon,
  Chip,
  DayGroupHeader,
  EmptyState,
  Kbd,
  PageNotesCard,
  Pagination,
  PillButton,
  Popover
} from '@netexknowledge/smart-components';
import PageNotesEntry from './PageNotesEntry';
import AnnotationsHelp, { translateWithTokens } from './AnnotationsHelp';
import { ANNOTATION_FILTERS, ANNOTATION_SHORTCUTS } from './annotationTypes';
import * as annotationsApi from '../../../services/api/annotationsApi';
import * as bookApi from '../../../services/api/bookApi';
import * as jwtService from '../../../services/jwt/jwtService';
import { Cmi5 } from '../../../services/cmi5/cmi5';
import { translateObject } from '../../../services/translate/customTranslateService';
import * as bookService from '../../../services/domain/bookService';
import * as bookActions from '../../../actions/bookActions';
import * as accountActions from '../../../actions/accountActions';
import {
  buildPageCards,
  findLessonInBook,
  flattenAnnotations,
  groupCardsByDay
} from '../../../services/domain/annotationsService';
import { getLogger } from '../../../services/logger';
import * as analyticsApi from '../../../services/api/analyticsApi';
import { ANALYTICS_CONTEXT, ANALYTICS_EVENT } from '../../../types/analytics';

const debug = getLogger('components:classroom:annotations');

export const PAGE_SIZE = 20;

const EMPTY_FILTER_SHORTCUTS = {
  highlight: ANNOTATION_SHORTCUTS.highlight,
  textNote: ANNOTATION_SHORTCUTS.textNote
};

const getEntryComponentActivityId = (entry, lessonActivityId) =>
  entry.type !== 'note' && entry.componentId ? `${lessonActivityId}/${entry.componentId}` : undefined;

const getEntryKey = entry => {
  if (entry.type === 'note') {
    return `note-${entry.activityId}`;
  }
  if (entry.type === 'highlight') {
    return `highlight-${entry.componentId || 'page'}`;
  }
  return `text-note-${entry.highlightId}`;
};

export class StudentAnnotations extends React.Component {
  static propTypes = {
    classroom: PropTypes.object,
    books: PropTypes.array,
    language: PropTypes.string,
    account: PropTypes.object,
    institution: PropTypes.object,
    emptyImg: PropTypes.string,
    bookActions: PropTypes.object,
    accountActions: PropTypes.object
  };

  state = {
    loading: true,
    entries: [],
    activeType: 'all',
    page: 0,
    titlesLoaded: false,
    titlesByActivityId: {},
    booksByActivityId: {}
  };

  rootRef = React.createRef();

  warnedCardKeys = new Set();

  componentDidMount() {
    this._isMounted = true;
    this.loadAnnotations();
  }

  componentDidUpdate(prevProps) {
    const classroomChanged = prevProps.classroom.id !== this.props.classroom.id;
    const booksLoaded = prevProps.books !== this.props.books && this.props.books;

    if (classroomChanged) {
      this.loadAnnotations();
    } else if (booksLoaded) {
      this.loadContentTitles(this.state.entries);
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  getSectionId() {
    const { classroom } = this.props;
    return classroom.sectionId || classroom.id;
  }

  trackMyNotesAccess() {
    const { classroom } = this.props;
    if (!classroom || !classroom.id) {
      return;
    }
    analyticsApi.trackAnalyticsEvent({
      event: ANALYTICS_EVENT.VIEW_MY_NOTES,
      classId: classroom.id,
      context: ANALYTICS_CONTEXT.MY_NOTES,
      status: 'success'
    });
  }

  async loadAnnotations() {
    const sectionId = this.getSectionId();
    const userId = jwtService.getUserId();

    if (!sectionId || !userId) {
      return;
    }

    this.warnedCardKeys = new Set();
    this.setState({ loading: true, activeType: 'all', page: 0, titlesLoaded: false });

    try {
      const [notes, highlights, noteHighlights] = await Promise.all([
        annotationsApi.getSectionNotes(sectionId, userId),
        annotationsApi.getSectionHighlights(sectionId, userId),
        annotationsApi.getSectionNoteHighlights(sectionId, userId)
      ]);

      const entries = flattenAnnotations(notes, highlights, noteHighlights);

      if (!this._isMounted) {
        return;
      }

      this.setState({ entries, loading: false });
      this.loadContentTitles(entries);
    } catch (error) {
      debug.error('[loadAnnotations] error loading annotations %s', error);
      if (this._isMounted) {
        this.setState({ entries: [], loading: false });
      }
    }
  }

  async loadContentTitles(entries) {
    const { books, classroom, language } = this.props;

    if (!books) {
      return;
    }

    const bookActivityIds = new Set(entries.map(entry => entry.bookActivityId));
    const booksWithAnnotations = books.filter(book => bookActivityIds.has(book.activityId));

    const titlesByActivityId = {};
    const booksByActivityId = {};

    await Promise.all(
      booksWithAnnotations.map(async book => {
        try {
          const res = await bookApi.getBook(classroom.id, book.id);
          booksByActivityId[book.activityId] = bookService.transformCmi5ToBook(res.data, book.id);
          const cmi5 = new Cmi5(res.data, book.id);
          cmi5.activities.forEach(activity => {
            if (activity.activityId && activity.title) {
              titlesByActivityId[activity.activityId] = translateObject(activity.title, language);
            }
          });
        } catch (error) {
          debug.error('[loadContentTitles] error loading book %s: %s', book.id, error);
        }
      })
    );

    if (this._isMounted) {
      this.setState(prevState => ({
        titlesLoaded: true,
        titlesByActivityId: { ...prevState.titlesByActivityId, ...titlesByActivityId },
        booksByActivityId: { ...prevState.booksByActivityId, ...booksByActivityId }
      }));
    }
  }

  getTitle(activityId) {
    const title = activityId ? this.state.titlesByActivityId[activityId] : null;
    if (title && typeof title === 'object') {
      return translateObject(title, this.props.language) || null;
    }
    return title || null;
  }

  getBookTitle(bookActivityId) {
    const { books } = this.props;
    const book = (books || []).find(candidate => candidate.activityId === bookActivityId);
    if (book && book.bookName) {
      return book.bookName;
    }
    return this.getTitle(bookActivityId) || I18n.t('annotations.unknownContent');
  }

  // A single-book classroom repeats the classroom title in every card, so the book is left out.
  isSingleBookClassroom() {
    const { books } = this.props;
    if (books) {
      return books.length === 1;
    }
    return new Set(this.state.entries.map(entry => entry.bookActivityId)).size === 1;
  }

  /**
   * "Unit › Lesson" (or "Book › Unit › Lesson" with several books). An unresolved lesson never shows
   * its id: the path stops at the unit and the card gets no links.
   */
  getCardPath(card) {
    const unitTitle = this.getTitle(card.unitActivityId);
    const lessonTitle = this.getTitle(card.lessonActivityId);
    const breadcrumb = [];

    if (!this.isSingleBookClassroom()) {
      breadcrumb.push(this.getBookTitle(card.bookActivityId));
    }
    if (unitTitle) {
      breadcrumb.push(unitTitle);
    }
    if (lessonTitle) {
      breadcrumb.push(lessonTitle);
    }
    if (!breadcrumb.length) {
      breadcrumb.push(this.getBookTitle(card.bookActivityId));
    }

    return { breadcrumb, resolved: !!lessonTitle };
  }

  warnUnresolvedCard(card) {
    if (this.warnedCardKeys.has(card.key)) {
      return;
    }
    this.warnedCardKeys.add(card.key);
    console.warn('[StudentAnnotations] lesson not found in book structure', {
      bookActivityId: card.bookActivityId,
      unitActivityId: card.unitActivityId,
      pageId: card.pageId
    });
  }

  async resolveBook(bookActivityId) {
    const { books, classroom } = this.props;
    const cachedBook = this.state.booksByActivityId[bookActivityId];

    if (cachedBook && cachedBook.content) {
      return cachedBook;
    }

    const bookMeta = (books || []).find(candidate => candidate.activityId === bookActivityId);
    if (!bookMeta) {
      return null;
    }

    const res = await bookApi.getBook(classroom.id, bookMeta.id);
    const book = bookService.transformCmi5ToBook(res.data, bookMeta.id);

    if (this._isMounted) {
      this.setState(prevState => ({
        booksByActivityId: { ...prevState.booksByActivityId, [bookActivityId]: book }
      }));
    }

    return book;
  }

  openAnnotationContent = (bookActivityId, activityId, questionActivityId) => {
    const { accountActions, bookActions, account, institution, classroom } = this.props;

    accountActions.forceCheckAndRefreshToken(async () => {
      try {
        const book = await this.resolveBook(bookActivityId);
        if (!book) {
          return;
        }

        const { lessonFound, unitFound } = findLessonInBook(book, activityId);
        if (!lessonFound || !unitFound) {
          return;
        }

        if (bookService.isLinkActivity(lessonFound.format)) {
          bookActions.openLink(lessonFound.url);
        } else {
          bookActions.openResource({
            book,
            unit: unitFound,
            lesson: lessonFound,
            account,
            institution,
            classroom,
            questionActivityId
          });
        }
        this.trackMyNotesAccess();
      } catch (error) {
        debug.error('[openAnnotationContent] error opening annotation content %s', error);
      }
    });
  };

  getCounts() {
    const counts = { all: 0 };
    this.state.entries.forEach(entry => {
      counts.all += 1;
      counts[entry.type] = (counts[entry.type] || 0) + 1;
    });
    return counts;
  }

  getFilteredCards() {
    const { entries, activeType } = this.state;
    return buildPageCards(activeType === 'all' ? entries : entries.filter(entry => entry.type === activeType));
  }

  setActiveType(activeType) {
    this.setState({ activeType, page: 0 });
  }

  changePage = page => {
    this.setState({ page });
    if (this.rootRef.current && this.rootRef.current.scrollIntoView) {
      this.rootRef.current.scrollIntoView({ block: 'start' });
    }
  };

  formatDayHeader(date) {
    if (date === null) {
      return I18n.t('annotations.noDate');
    }
    const day = dayjs(date);
    const formatted = day.format(I18n.t('annotations.dateFormat'));
    return day.isSame(dayjs(), 'day') ? `${I18n.t('annotations.today')} · ${formatted}` : formatted;
  }

  formatCardTime(timestamp) {
    if (!timestamp) {
      return {};
    }
    const date = dayjs(timestamp);
    const time = date.format('HH:mm');
    const timeLabel = date.isSame(dayjs(), 'day')
      ? `${I18n.t('annotations.today')}, ${time}`
      : date.format(I18n.t('annotations.dateTimeFormat'));
    return { timeLabel, mobileTimeLabel: time };
  }

  renderCard(card) {
    const { titlesLoaded } = this.state;
    const { breadcrumb, resolved } = this.getCardPath(card);
    const unavailable = titlesLoaded && !resolved;
    const canOpen = titlesLoaded && resolved;

    if (unavailable) {
      this.warnUnresolvedCard(card);
    }

    return (
      <PageNotesCard
        key={card.key}
        breadcrumb={breadcrumb}
        loading={!titlesLoaded}
        {...this.formatCardTime(card.date)}
        actionLabel={I18n.t('annotations.goToPage')}
        onAction={canOpen ? () => this.openAnnotationContent(card.bookActivityId, card.lessonActivityId) : undefined}
        disabledLabel={unavailable ? I18n.t('annotations.unknownContent') : undefined}
        moreLabel={count => I18n.t('annotations.showMore', { count })}
        lessLabel={I18n.t('annotations.showLess')}
      >
        {card.entries.map(entry => (
          <PageNotesEntry
            key={getEntryKey(entry)}
            entry={entry}
            onOpen={
              canOpen
                ? () =>
                    this.openAnnotationContent(
                      card.bookActivityId,
                      card.lessonActivityId,
                      getEntryComponentActivityId(entry, card.lessonActivityId)
                    )
                : undefined
            }
          />
        ))}
      </PageNotesCard>
    );
  }

  renderHelp() {
    return (
      <div className="annotations-help">
        <Popover
          title={I18n.t('annotations.help.title')}
          closeLabel={I18n.t('annotations.help.close')}
          trigger={
            <PillButton label={I18n.t('annotations.help.title')} icon={<i className="custom-icons-question-mark-circle" />} />
          }
        >
          <AnnotationsHelp compact />
        </Popover>
      </div>
    );
  }

  renderFilters() {
    const { activeType } = this.state;
    const counts = this.getCounts();

    return (
      <div className="annotations-filters-row">
        {this.renderHelp()}
        <div className="annotations-filters">
          {ANNOTATION_FILTERS.map(({ type, labelKey, colors }) => (
            <Chip
              key={type}
              label={I18n.t(labelKey)}
              count={counts[type] || 0}
              dotColor={colors && colors.dot}
              background={colors && colors.background}
              borderColor={colors && colors.border}
              textColor={colors && colors.text}
              filledWhenActive
              active={activeType === type}
              onClick={() => this.setActiveType(type)}
            />
          ))}
        </div>
      </div>
    );
  }

  renderFilterEmpty() {
    const { activeType } = this.state;
    const colors = ANNOTATION_FILTERS.find(filter => filter.type === activeType).colors;
    const shortcut = EMPTY_FILTER_SHORTCUTS[activeType];

    return (
      <EmptyState
        size="small"
        icon={<AnnotationTypeIcon type={activeType} size={22} color={colors.icon} />}
        iconBackground={colors.background}
        title={I18n.t(`annotations.emptyFilter.${activeType}.title`)}
        description={translateWithTokens(`annotations.emptyFilter.${activeType}.description`)}
        actionLabel={I18n.t('annotations.emptyFilter.showAll')}
        onAction={() => this.setActiveType('all')}
      >
        {shortcut && <Kbd>{shortcut}</Kbd>}
      </EmptyState>
    );
  }

  renderCards() {
    const cards = this.getFilteredCards();
    const total = Math.ceil(cards.length / PAGE_SIZE);
    const page = Math.min(this.state.page, Math.max(total - 1, 0));
    const from = page * PAGE_SIZE;
    const pageCards = cards.slice(from, from + PAGE_SIZE);

    return (
      <>
        {groupCardsByDay(pageCards).map(group => (
          <div className="annotations-day" key={group.date === null ? 'no-date' : group.date}>
            <DayGroupHeader label={this.formatDayHeader(group.date)} />
            <div className="annotations-day-cards">{group.cards.map(card => this.renderCard(card))}</div>
          </div>
        ))}
        <div className="annotations-pagination">
          <Pagination
            page={page}
            total={total}
            onChange={this.changePage}
            summary={I18n.t('annotations.pagination.summary', { from: from + 1, to: from + pageCards.length, total: cards.length })}
            compactLabel={I18n.t('annotations.pagination.compact', { page: page + 1, total })}
            prevLabel={I18n.t('annotations.pagination.prev')}
            nextLabel={I18n.t('annotations.pagination.next')}
            pageAriaLabel={pageNumber => I18n.t('annotations.pagination.page', { page: pageNumber })}
          />
        </div>
      </>
    );
  }

  render() {
    const { loading, entries, activeType } = this.state;
    const { emptyImg } = this.props;

    if (loading) {
      return (
        <div className="student-annotations loading">
          <CircularProgress />
        </div>
      );
    }

    if (!entries.length) {
      return (
        <div className="student-annotations">
          <EmptyState
            media={emptyImg ? <img className="annotations-empty-image" src={emptyImg} alt="" /> : null}
            title={I18n.t('annotations.emptyTitle')}
            description={I18n.t('annotations.emptyDescription')}
          >
            <AnnotationsHelp framed />
          </EmptyState>
        </div>
      );
    }

    const hasFilteredEntries = activeType === 'all' || entries.some(entry => entry.type === activeType);

    return (
      <div className="student-annotations" ref={this.rootRef}>
        {this.renderFilters()}
        {hasFilteredEntries ? this.renderCards() : this.renderFilterEmpty()}
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    classroom: state.classrooms.selectedClassroom.classroom,
    books: state.book.books,
    language: state.account.user.language,
    account: state.account,
    institution: state.school.school,
    emptyImg: state.theme.images.placeholders.empty
  };
}

function mapDispatchToProps(dispatch) {
  return {
    bookActions: bindActionCreators(bookActions, dispatch),
    accountActions: bindActionCreators(accountActions, dispatch)
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(StudentAnnotations);
